import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { ModalActions } from '@rover/pages/src/bright-horizons/HubPage/components/modals/ModalTypes.common';
import { useApiBrightHorizonsHubScreenRetrieve } from '@rover/rsdk/src/apiClient/latest/api-bright-horizons-hub-screen-retrieve';
import type { BrightHorizonsAction } from '@rover/rsdk/src/apiClient/latest/model/brightHorizonsAction';

export const ModalProviderContext = createContext<ModalActions>({} as ModalActions);

export const ModalProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data } = useApiBrightHorizonsHubScreenRetrieve();
  const [modalAction, setModalAction] = useState<BrightHorizonsAction | null>(null);
  const [hasDismissedExpiringCreditsModal, setHasDismissedExpiringCreditsModal] = useState(false);

  useEffect(() => {
    // If a modalToShow is set, it means we want to show it on page load
    if (data?.modalToShow) {
      if (
        data.modalToShow.actionType === 'open_expiring_credits' &&
        hasDismissedExpiringCreditsModal
      ) {
        return;
      }
      setModalAction(data.modalToShow);
    }
  }, [data?.modalToShow, hasDismissedExpiringCreditsModal]);

  const openModal = (action: BrightHorizonsAction): void => setModalAction(action);

  const closeModal = useCallback((): void => {
    if (modalAction?.actionType === 'open_expiring_credits')
      setHasDismissedExpiringCreditsModal(true);
    setModalAction(null);
  }, [modalAction]);

  // Context value
  const value = useMemo(
    (): ModalActions => ({
      openModal,
      closeModal,
      modalAction,
      hasDismissedExpiringCreditsModal,
    }),
    [modalAction, closeModal, hasDismissedExpiringCreditsModal]
  );

  return <ModalProviderContext.Provider value={value}>{children}</ModalProviderContext.Provider>;
};

const useModalProviderContext = (): ModalActions => {
  const context = useContext(ModalProviderContext);
  if (!context) {
    throw new Error('useModalProviderContext must be used within a ModalProvider');
  }
  return context;
};

export default useModalProviderContext;
